import React from 'react';
import cn from 'classnames';
import { PasswordRequirements as PasswordReqs, PasswordStrength } from '../../utils/validation';
import styles from './PasswordRequirements.module.scss';

interface PasswordRequirementsProps {
  requirements: PasswordReqs;
  strength: PasswordStrength;
  strengthLabel: string;
  show: boolean;
}

interface RequirementItemProps {
  met: boolean;
  label: string;
}

const RequirementItem: React.FC<RequirementItemProps> = ({ met, label }) => (
  <li className={cn(styles.requirement, { [styles.met]: met })}>
    <span className={styles.icon}>{met ? '\u2713' : '\u25CB'}</span>
    <span className={styles.label}>{label}</span>
  </li>
);

const getStrengthClass = (strength: PasswordStrength): string => {
  switch (strength) {
    case PasswordStrength.VeryWeak:
      return styles.veryWeak;
    case PasswordStrength.Weak:
      return styles.weak;
    case PasswordStrength.Fair:
      return styles.fair;
    case PasswordStrength.Strong:
      return styles.strong;
    case PasswordStrength.VeryStrong:
      return styles.veryStrong;
    default:
      return '';
  }
};

export const PasswordRequirementsDisplay: React.FC<PasswordRequirementsProps> = ({
  requirements,
  strength,
  strengthLabel,
  show,
}) => {
  if (!show) {
    return null;
  }

  const strengthPercentage = ((strength + 1) / 5) * 100;

  return (
    <div className={styles.container}>
      <div className={styles.strengthBar}>
        <div className={styles.strengthLabel}>
          Password Strength: <span className={getStrengthClass(strength)}>{strengthLabel}</span>
        </div>
        <div className={styles.barBackground}>
          <div
            className={cn(styles.barFill, getStrengthClass(strength))}
            style={{ width: `${strengthPercentage}%` }}
          />
        </div>
      </div>

      <ul className={styles.requirementsList}>
        <RequirementItem met={requirements.minLength} label="At least 8 characters" />
        <RequirementItem met={requirements.hasUppercase} label="One uppercase letter (A-Z)" />
        <RequirementItem met={requirements.hasLowercase} label="One lowercase letter (a-z)" />
        <RequirementItem met={requirements.hasNumber} label="One number (0-9)" />
        <RequirementItem met={requirements.hasSpecial} label="One special character (!@#$%^&*)" />
      </ul>
    </div>
  );
};

export default PasswordRequirementsDisplay;
